import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MagnifyingGlass, FilePdf, FileXls, Printer, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * ReportToolbar — drop into every Accounts / Finance / Inventory tab to get a
 * consistent Search + Date Range + PDF + Excel + Print bar.
 *
 * Props:
 *   - search, onSearch     — controlled search box (omit to hide)
 *   - searchPlaceholder
 *   - startDate, endDate, onStartDate, onEndDate — date range filters (omit pair to hide)
 *   - extraFilters         — slot for additional <Select>/<Input> filter widgets
 *   - exportUrl(format)    — function(format: 'pdf'|'xlsx') => full URL of the report
 *                            (used by PDF + Excel buttons). Omit to hide them.
 *   - onPrint              — optional handler for the Print button (defaults to window.print)
 *   - downloadBaseName     — base filename for downloads
 *   - testidPrefix         — used to compose data-testids ("toolbar-trial-balance"…)
 */
export default function ReportToolbar({
  search,
  onSearch,
  searchPlaceholder = 'Search…',
  startDate,
  endDate,
  onStartDate,
  onEndDate,
  extraFilters = null,
  exportUrl,
  onPrint,
  downloadBaseName = 'report',
  testidPrefix = 'report-toolbar',
}) {
  const downloadFile = async (format) => {
    if (!exportUrl) return;
    const url = exportUrl(format);
    if (!url) return;
    try {
      const token =
        localStorage.getItem('erp_token') ||
        localStorage.getItem('access_token') ||
        localStorage.getItem('token');
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${downloadBaseName}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(`Export failed: ${e.message || 'unknown error'}`);
    }
  };

  const showSearch = onSearch !== undefined;
  const showDateRange = onStartDate !== undefined && onEndDate !== undefined;
  const showExports = !!exportUrl;

  return (
    <div
      className="flex flex-wrap items-end gap-2 mb-3 p-3 rounded-lg border bg-muted/20"
      data-testid={testidPrefix}
    >
      {showSearch && (
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Search</Label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search || ''}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-7 h-9"
              data-testid={`${testidPrefix}-search`}
            />
          </div>
        </div>
      )}

      {showDateRange && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">From</Label>
            <Input
              type="date"
              value={startDate || ''}
              onChange={(e) => onStartDate(e.target.value)}
              className="h-9 w-40"
              data-testid={`${testidPrefix}-start-date`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">To</Label>
            <Input
              type="date"
              value={endDate || ''}
              onChange={(e) => onEndDate(e.target.value)}
              className="h-9 w-40"
              data-testid={`${testidPrefix}-end-date`}
            />
          </div>
        </>
      )}

      {extraFilters && (
        <div className="flex items-end gap-2">
          <span className="text-muted-foreground"><Funnel size={14} /></span>
          {extraFilters}
        </div>
      )}

      <div className="flex-1" />

      <div className="flex items-end gap-1.5">
        {showExports && (
          <>
            <Button
              size="sm" variant="outline" className="h-9"
              onClick={() => downloadFile('pdf')}
              data-testid={`${testidPrefix}-pdf`}
            >
              <FilePdf size={14} className="mr-1.5" /> PDF
            </Button>
            <Button
              size="sm" variant="outline" className="h-9"
              onClick={() => downloadFile('xlsx')}
              data-testid={`${testidPrefix}-xlsx`}
            >
              <FileXls size={14} className="mr-1.5" /> Excel
            </Button>
          </>
        )}
        <Button
          size="sm" variant="outline" className="h-9"
          onClick={onPrint || (() => window.print())}
          data-testid={`${testidPrefix}-print`}
        >
          <Printer size={14} className="mr-1.5" /> Print
        </Button>
      </div>
    </div>
  );
}
